import React, { useState, useContext, useEffect, useRef } from 'react';
import api from '../../http';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import './style/AuthorizationModal.css';
import { Context } from '../../index';

function AuthorizationModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({ login: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { user } = useContext(Context);
  const loginRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setFormData({ login: '', password: '' });
      setShowPassword(false);
      setError('');
      setLoading(false);
      return;
    }
    const t = setTimeout(() => loginRef.current?.focus(), 50);
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      clearTimeout(t);
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!formData.login.trim() || !formData.password.trim()) {
      setError('Заполните логин и пароль');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data } = await api.post('v1/login', formData);
      user.hydrateFromAuth(data);
      onClose();
    } catch (err) {
      const status = err.response?.status;
      if (status === 401 || status === 400)      setError('Неверный логин или пароль');
      else if (status === 403)                   setError(err.response?.data?.message || 'Доступ запрещён');
      else if (status === 429)                   setError('Слишком много попыток. Подождите 15 минут');
      else if (status >= 500)                    setError('Сервер недоступен');
      else                                       setError('Не удалось войти');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="authv2-overlay" onClick={onClose} role="presentation">
      <div
        className="authv2"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="authv2-title"
      >
        {/* Brand panel — слева (desktop), сверху (mobile) */}
        <aside className="authv2__brand" aria-hidden="true">
          <div className="authv2__brand-mark">MB4</div>
          <div className="authv2__brand-body">
            <h2 className="authv2__brand-title">Внутренняя CRM для&nbsp;управления заказами</h2>
            <p className="authv2__brand-text">
              Заявки, договоры, производство и&nbsp;логистика —
              в&nbsp;одном&nbsp;окне.
            </p>
          </div>
          <ul className="authv2__brand-list">
            <li>Контроль заказов от&nbsp;заявки до&nbsp;доставки</li>
            <li>Производство и&nbsp;планирование путевых&nbsp;листов</li>
            <li>Договоры и&nbsp;остатки по&nbsp;каждому клиенту</li>
          </ul>
          <div className="authv2__brand-foot">© MB4 · 2026</div>
        </aside>

        {/* Form panel */}
        <section className="authv2__form-wrap">
          <button
            type="button"
            className="authv2__close"
            onClick={onClose}
            aria-label="Закрыть"
          >
            <CloseIcon sx={{ fontSize: 22 }} />
          </button>

          <header className="authv2__form-head">
            <h1 id="authv2-title" className="authv2__title">Вход</h1>
            <p className="authv2__subtitle">Используйте корпоративные учётные данные</p>
          </header>

          <form className="authv2__form" onSubmit={handleSubmit} noValidate>
            <label className="authv2__field">
              <span className="authv2__label">Логин</span>
              <input
                ref={loginRef}
                type="text"
                name="login"
                autoComplete="username"
                placeholder="например, ivan.smirnov"
                className={`authv2__input ${error ? 'is-error' : ''}`}
                value={formData.login}
                onChange={handleChange}
                disabled={loading}
              />
            </label>

            <label className="authv2__field">
              <span className="authv2__label">Пароль</span>
              <div className="authv2__input-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  autoComplete="current-password"
                  placeholder="Введите пароль"
                  className={`authv2__input ${error ? 'is-error' : ''}`}
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="authv2__eye"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                  tabIndex={-1}
                >
                  {showPassword
                    ? <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />
                    : <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />}
                </button>
              </div>
            </label>

            {error && <div className="authv2__error" role="alert">{error}</div>}

            <div className="authv2__row">
              <label className="authv2__check">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  disabled={loading}
                />
                <span>Запомнить меня</span>
              </label>
            </div>

            <button
              type="submit"
              className="authv2__submit"
              disabled={loading}
            >
              {loading
                ? <span className="authv2__spinner" aria-hidden="true" />
                : <>
                    <span>Войти в&nbsp;систему</span>
                    <ArrowForwardIcon sx={{ fontSize: 18 }} />
                  </>}
            </button>

            <p className="authv2__foot">
              Доступ выдаёт администратор системы
            </p>
          </form>
        </section>
      </div>
    </div>
  );
}

export default AuthorizationModal;
